/**
 * TestModeToggle — director-scoped test-mode switch for /conflict-search.
 *
 * Purpose: give directors a way to probe the conflict-search flow on
 * live prod WITHOUT polluting real Cosmos rows. When ON, every axios
 * request from this browser session includes X-Bonsai-Test-Mode: 1,
 * which the backend middleware uses to route reads and writes to
 * <collection>_test collections (see backend/hub/test_mode.py).
 *
 * Persistence: sessionStorage — survives navigations, resets on tab
 * close. Deliberately NOT localStorage: we do not want test-mode to
 * silently persist across sessions.
 *
 * Visibility: only rendered for accounts in ADMIN_EMAILS. Non-admin
 * accounts that somehow set the flag get 403 from the backend and
 * the flag is cleared client-side.
 *
 * UI: compact top-right pill. Green "LIVE" when off, amber "TEST" when
 * on. Clicking toggles. A wider banner is rendered below on
 * /conflict-search itself when on, so directors cannot forget which
 * mode they are in.
 */

import { useCallback, useState } from "react";
import { AlertTriangle, Beaker } from "lucide-react";
import { useCurrentUser } from "../hooks/useCurrentUser";

const STORAGE_KEY = "bonsai_test_mode";

export function useTestMode() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return typeof window !== "undefined"
        && window.sessionStorage
        && window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    try {
      if (enabled) {
        window.sessionStorage.removeItem(STORAGE_KEY);
        setEnabled(false);
      } else {
        window.sessionStorage.setItem(STORAGE_KEY, "1");
        setEnabled(true);
      }
    } catch {
      // sessionStorage unavailable — cannot persist, cannot toggle.
    }
  }, [enabled]);

  return { enabled, toggle };
}

export default function TestModeToggle() {
  const { enabled, toggle } = useTestMode();
  const { isAdmin } = useCurrentUser();

  if (!isAdmin) {
    // Non-admins never see the toggle. If somehow their session had
    // the flag set, clear it defensively so their requests are not
    // stamped with a header the backend will 403 on.
    if (enabled) {
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch {}
    }
    return null;
  }

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="test-mode-toggle"
      className={
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors "
        + (enabled
          ? "bg-status-amber-dim/40 border-amber-500/60 text-status-amber hover:bg-status-amber-dim/60"
          : "bg-teal-dim/30 border-teal/50 text-teal hover:bg-teal-dim/50")
      }
      title={enabled
        ? "Test mode is ON — writes go to _test collections. Click to switch back to LIVE."
        : "LIVE mode — writes go to production. Click to switch to TEST mode."}
    >
      {enabled ? <AlertTriangle className="w-3.5 h-3.5" /> : <Beaker className="w-3.5 h-3.5" />}
      {enabled ? "TEST" : "LIVE"}
    </button>
  );
}

export function TestModeBanner() {
  const { enabled } = useTestMode();
  if (!enabled) return null;
  return (
    <div
      data-testid="test-mode-banner"
      className="mb-3 px-3 py-2 rounded-md bg-status-amber-dim/40 border border-amber-500/60 text-status-amber text-xs flex items-center gap-2"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>
        <strong>TEST MODE.</strong> Reads and writes on the conflict-search flow are routed to <code>_test</code> collections. Production data is not touched. Toggle off (top of page) to return to live.
      </span>
    </div>
  );
}
